import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import PageContainer from './components/PageContainer';
import PageHeader from './components/PageHeader';
import GradientButton from './components/GradientButton';
import GradientIcon from './components/GradientIcon';
import Badge from './components/Badge';
import ProgressBar from './components/ProgressBar';
import TabbedResults from './components/TabbedResults';
import { getSkincareRecommendations } from './services/openaiService';
import safetyTips from './data/safetyTips';

const SkincareRecommender = () => {
  const [showHome, setShowHome] = useState(true);
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState({
    gender: '', age: '', city: '', climate: '', shininess: '',
    dryness: '', concerns: [], conditions: [], reactions: '',
    routine: '', preferences: []
  });
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [error, setError] = useState(null);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  // Combine all tips for loading screen rotation
  const allTips = [
    ...safetyTips.safety_notes,
    ...safetyTips.application_tips,
    ...safetyTips.additional_tips
  ];

  const indianCities = {
    Mumbai: 'Hot and Humid',
    Delhi: 'Hot and Dry',
    Bangalore: 'Moderate',
    Chennai: 'Hot and Humid',
    Kolkata: 'Hot and Humid',
    Hyderabad: 'Hot and Dry',
    Pune: 'Moderate',
    Ahmedabad: 'Hot and Dry',
    Jaipur: 'Hot and Dry',
    Other: 'Select Climate'
  };

  const questions = [
    { id: 'gender', question: 'I am...', type: 'single', options: ['Male', 'Female', 'Prefer not to say'] },
    { id: 'age', question: 'My age is...', type: 'single', options: ['Under 18', '18-25', '26-35', '36-45', '46+'] },
    { id: 'city', question: 'Which city do you live in?', type: 'dropdown', options: Object.keys(indianCities) },
    { 
      id: 'climate', 
      question: 'Select your climate type:',
      type: 'climate-select',
      options: ['Hot and Humid', 'Hot and Dry', 'Cold and Dry', 'Moderate']
    },
    { 
      id: 'shininess', question: 'How does your face feel 2-3 hours after washing?', type: 'single',
      options: [
        { value: 'very-shiny', label: 'Very shiny and oily all over' },
        { value: 'tzone-shiny', label: 'Shiny on forehead, nose and chin' },
        { value: 'normal', label: 'Comfortable, not oily or tight' },
        { value: 'tight', label: 'Tight and uncomfortable' }
      ]
    },
    {
      id: 'dryness', question: 'Do you experience any of these?', type: 'single',
      options: [
        { value: 'flaky', label: 'Dry patches or flaking' },
        { value: 'rough', label: 'Rough texture' },
        { value: 'neither', label: 'Neither, my skin feels smooth' }
      ]
    },
    {
      id: 'concerns', question: 'What bothers you most about your skin?',
      description: 'Select up to 3', type: 'multiple', max: 3,
      options: ['Pimples and breakouts', 'Dark spots or uneven tone', 'Dull and tired-looking skin',
                'Fine lines or aging signs', 'Large pores', 'Dark circles under eyes', 'Nothing specific']
    },
    {
      id: 'conditions', question: 'Do you have any of these skin conditions?',
      description: 'Select all that apply (optional)', type: 'multiple', max: 5, optional: true,
      options: ['Active acne', 'Eczema or dermatitis', 'Rosacea', 'Melasma or stubborn pigmentation', 'None of these']
    },
    {
      id: 'reactions', question: 'Have you had bad reactions to skincare before?',
      type: 'single', optional: true,
      options: [
        { value: 'fragrance', label: 'Yes, to fragranced products' },
        { value: 'acids', label: 'Yes, to acids (stinging/burning)' },
        { value: 'retinoids', label: 'Yes, to retinoids (severe dryness/peeling)' },
        { value: 'none', label: 'No bad reactions' },
        { value: 'unsure', label: 'Not sure' }
      ]
    },
    {
      id: 'routine', question: 'How much time can you spend daily?', type: 'single',
      options: [
        { value: 'minimal', label: '5 minutes (Quick and simple)' },
        { value: 'moderate', label: '10 minutes (Balanced routine)' },
        { value: 'comprehensive', label: '15+ minutes (Complete care)' }
      ]
    },
    {
      id: 'preferences', question: 'Any product preferences?', description: 'Select all that apply (optional)',
      type: 'multiple', max: 5, optional: true,
      options: ['Vegan products only', 'Cruelty-free brands', 'Fragrance-free', 
                'Pregnancy-safe ingredients', 'Korean beauty (K-beauty)', 
                'Indian brands preferred', 'No preferences']
    }
  ];

  const determineSkinType = () => {
    const { shininess, dryness } = responses;
    if (shininess === 'very-shiny') return 'oily';
    if (shininess === 'tight' || dryness === 'flaky' || dryness === 'rough') return 'dry';
    if (shininess === 'tzone-shiny') return 'combination';
    return 'normal';
  };

  const getProductBadges = (productName) => {
    const badges = [];
    const lower = productName.toLowerCase();
    const vegan = ['minimalist', 'plum', 'dot & key', 'mamaearth'];
    if (vegan.some(b => lower.includes(b))) badges.push('Vegan');
    const cruelty = ['minimalist', 'plum', 'cosrx', 'mamaearth'];
    if (cruelty.some(b => lower.includes(b))) badges.push('Cruelty-Free');
    return badges;
  };

  const getProductInfo = (productName, productType) => {
    const prices = {
      Cleanser: { price: 499, quantity: '100ml', duration: '2 months' },
      Serum: { price: 699, quantity: '30ml', duration: '2-3 months' },
      Treatment: { price: 599, quantity: '30ml', duration: '3 months' },
      'Night Serum': { price: 799, quantity: '30ml', duration: '2-3 months' },
      'Eye Cream': { price: 649, quantity: '15ml', duration: '3 months' },
      Moisturizer: { price: 549, quantity: '50ml', duration: '2 months' },
      'Night Cream': { price: 649, quantity: '50ml', duration: '2 months' },
      'Rich Hydrating Cream': { price: 599, quantity: '50ml', duration: '2 months' },
      'Oil-Free Gel Moisturizer': { price: 549, quantity: '50ml', duration: '2 months' },
      'Light Daily Moisturizer': { price: 499, quantity: '50ml', duration: '2 months' },
      Sunscreen: { price: 599, quantity: '50ml', duration: '1-2 months' },
      Toner: { price: 449, quantity: '100ml', duration: '2 months' },
      Exfoliator: { price: 549, quantity: '50ml', duration: '3 months' },
      'Face Mask': { price: 399, quantity: '100g', duration: '2 months' },
      'Lip Balm': { price: 249, quantity: '10g', duration: '3 months' },
      'Face Oil': { price: 799, quantity: '30ml', duration: '2-3 months' }
    };

    const lower = productName.toLowerCase();
    let basePrice = prices[productType]?.price || 549;

    // Updated brand pricing - 2025 pricing
    if (lower.includes('minimalist')) basePrice = Math.round(basePrice * 0.78);
    else if (lower.includes('ordinary')) basePrice = Math.round(basePrice * 0.68);
    else if (lower.includes('cosrx')) basePrice = Math.round(basePrice * 1.45);
    else if (lower.includes('innisfree')) basePrice = Math.round(basePrice * 1.35);
    else if (lower.includes('etude house')) basePrice = Math.round(basePrice * 1.25);
    else if (lower.includes('some by mi')) basePrice = Math.round(basePrice * 1.35);
    else if (lower.includes('laneige')) basePrice = Math.round(basePrice * 2.3);
    else if (lower.includes('beauty of joseon')) basePrice = Math.round(basePrice * 1.55);
    else if (lower.includes('plum')) basePrice = Math.round(basePrice * 0.92);
    else if (lower.includes('dot & key') || lower.includes('dot and key')) basePrice = Math.round(basePrice * 0.98);
    else if (lower.includes('mamaearth')) basePrice = Math.round(basePrice * 0.88);
    else if (lower.includes('pilgrim')) basePrice = Math.round(basePrice * 0.90);
    else if (lower.includes('wow')) basePrice = Math.round(basePrice * 0.85);
    else if (lower.includes('neutrogena')) basePrice = Math.round(basePrice * 1.15);
    else if (lower.includes('cetaphil')) basePrice = Math.round(basePrice * 1.20);
    else if (lower.includes('simple')) basePrice = Math.round(basePrice * 0.95);
    else if (lower.includes('nivea')) basePrice = Math.round(basePrice * 0.90);
    else if (lower.includes('ponds')) basePrice = Math.round(basePrice * 0.88);
    else if (lower.includes('olay')) basePrice = Math.round(basePrice * 1.28);
    else if (lower.includes('requil') || lower.includes('re\'equil')) basePrice = Math.round(basePrice * 1.08);
    else if (lower.includes('derma co')) basePrice = Math.round(basePrice * 0.95);
    else if (lower.includes('aqualogica')) basePrice = Math.round(basePrice * 0.98);
    else if (lower.includes('forest essentials')) basePrice = Math.round(basePrice * 2.6);
    else if (lower.includes('kama ayurveda')) basePrice = Math.round(basePrice * 2.4);
    else if (lower.includes('biotique')) basePrice = Math.round(basePrice * 0.78);
    else if (lower.includes('khadi')) basePrice = Math.round(basePrice * 0.72);
    else if (lower.includes('juicy chemistry')) basePrice = Math.round(basePrice * 1.85);
    else if (lower.includes('earth rhythm')) basePrice = Math.round(basePrice * 0.95);
    else if (lower.includes('fix my curls')) basePrice = Math.round(basePrice * 0.88);
    else if (lower.includes('conscious chemist')) basePrice = Math.round(basePrice * 1.10);
    else if (lower.includes('deconstruct')) basePrice = Math.round(basePrice * 0.82);
    else if (lower.includes('suganda')) basePrice = Math.round(basePrice * 0.85);
    else if (lower.includes('dr. sheth')) basePrice = Math.round(basePrice * 1.05);
    else if (lower.includes('fix derma')) basePrice = Math.round(basePrice * 0.92);
    else if (lower.includes('joy')) basePrice = Math.round(basePrice * 0.75);
    else if (lower.includes('chemist at play')) basePrice = Math.round(basePrice * 0.88);
    else if (lower.includes('be minimalist')) basePrice = Math.round(basePrice * 0.78);
    else if (lower.includes('foxtale')) basePrice = Math.round(basePrice * 0.95);
    else if (lower.includes('earth rhythm')) basePrice = Math.round(basePrice * 0.95);
    else if (lower.includes('reequil')) basePrice = Math.round(basePrice * 1.08);
    else if (lower.includes('derma essentia')) basePrice = Math.round(basePrice * 1.12);
    else if (lower.includes('facescanada')) basePrice = Math.round(basePrice * 0.82);
    else if (lower.includes('la shield')) basePrice = Math.round(basePrice * 1.15);
    else if (lower.includes('tvam')) basePrice = Math.round(basePrice * 0.85);
    else if (lower.includes('la roche-posay') || lower.includes('la roche posay')) basePrice = Math.round(basePrice * 2.8);

    return {
      price: basePrice,
      quantity: prices[productType]?.quantity || '50ml',
      duration: prices[productType]?.duration || '2 months'
    };
  };

  const getNykaaSearchUrl = (name) => {
    return `https://www.nykaa.com/search/result/?q=${encodeURIComponent(name)}`;
  };

  const getTiraSearchUrl = (name) => {
    return `https://www.tirabeauty.com/products/?q=${encodeURIComponent(name)}`;
  };

  const calculateTotalCost = (recommendations) => {
    let total = 0;
    const uniqueProducts = new Set();
    
    recommendations.morning.forEach(item => {
      const key = `${item.name}-${item.example}`;
      if (!uniqueProducts.has(key)) {
        uniqueProducts.add(key);
        total += item.price || 0;
      }
    });
    
    recommendations.evening.forEach(item => {
      const key = `${item.name}-${item.example}`;
      if (!uniqueProducts.has(key)) {
        uniqueProducts.add(key);
        total += item.price || 0;
      }
    });
    
    return total;
  };

  const handleResponse = (questionId, value) => {
    const currentQ = questions[step];
    if (currentQ.type === 'multiple') {
      const current = responses[questionId] || [];
      const max = currentQ.max || Infinity;
      if (current.includes(value)) {
        setResponses({ ...responses, [questionId]: current.filter(c => c !== value) });
      } else if (current.length < max) {
        setResponses({ ...responses, [questionId]: [...current, value] });
      }
    } else if (questionId === 'city') {
      setResponses({ ...responses, city: value, climate: indianCities[value] });
    } else {
      setResponses({ ...responses, [questionId]: value });
    }
  };

  const canProceed = () => {
    const q = questions[step];
    if (q.optional) return true;
    if (q.id === 'climate' && responses.city && responses.city !== 'Other') return true;
    const resp = responses[q.id];
    if (q.type === 'multiple') return resp && resp.length > 0;
    return resp !== '';
  };

  const fetchAIRecommendations = async () => {
    setIsLoadingRecommendations(true);
    setError(null);

    try {
      const recommendations = await getSkincareRecommendations(responses);
      console.log('🔍 AI Recommendations received:', recommendations);
      console.log('🔍 Morning routine first item:', recommendations.morning_routine?.[0]);
      console.log('🔍 Has options?', recommendations.morning_routine?.[0]?.options ? 'YES' : 'NO');
      setAiRecommendations(recommendations);
    } catch (err) {
      setError(err.message || 'Failed to get recommendations. Please try again.');

      // Fall back to the old generateRecommendations function
      const fallbackRecs = generateRecommendations();
      setAiRecommendations({
        skin_type: determineSkinType(),
        analysis: `Based on your responses, you have ${determineSkinType()} skin.`,
        morning_routine: fallbackRecs.morning.map(item => ({
          step: item.step,
          category: item.name,
          product_name: item.example,
          brand: item.example.split(' ')[0],
          key_ingredients: [],
          why: item.why,
          price: item.price,
          quantity: item.quantity,
          duration: item.duration,
          badges: item.badges || []
        })),
        evening_routine: fallbackRecs.evening.map(item => ({
          step: item.step,
          category: item.name,
          product_name: item.example,
          brand: item.example.split(' ')[0],
          key_ingredients: [],
          why: item.why,
          price: item.price,
          quantity: item.quantity,
          duration: item.duration,
          badges: item.badges || []
        })),
        additional_tips: [],
        expected_results: {},
        total_cost: calculateTotalCost(fallbackRecs)
      });
    } finally {
      setIsLoadingRecommendations(false);
    }
  };

  const generateRecommendations = () => {
    const skinType = determineSkinType();
    const { concerns = [], age, climate, preferences = [] } = responses;
    const isPreg = preferences.includes('Pregnancy-safe ingredients');
    const korean = preferences.includes('Korean beauty (K-beauty)');
    const indian = preferences.includes('Indian brands preferred');

    // Always use budget tier as default since users can toggle in the UI
    const budgetTier = 'budget';

    const recs = { morning: [], evening: [] };

    let cleanser = 'Minimalist Salicylic Acid Face Wash';
    if (skinType === 'oily') {
      if (budgetTier === 'premium') {
        cleanser = korean ? 'Laneige Cream Skin Cleanser' : indian ? 'Forest Essentials Facial Cleanser' : 'La Roche-Posay Effaclar Gel Cleanser';
        cleanser = korean ? 'COSRX Low pH Good Morning Gel Cleanser' : indian ? 'Dr. Sheth\'s Salicylic Acid Cleanser' : 'Neutrogena Deep Clean Foam Cleanser';
      } else if (korean) {
        cleanser = 'COSRX Low pH Good Morning Gel Cleanser';
      } else if (indian) {
        cleanser = 'Minimalist Salicylic Acid Face Wash';
      }
      const info = getProductInfo(cleanser, 'Cleanser');
      recs.morning.push({ step: '1', name: 'Cleanser', product: 'Salicylic Acid Face Wash',
        example: cleanser, why: 'Removes excess oil', badges: getProductBadges(cleanser), ...info });
      recs.evening.push({ step: '1', name: 'Cleanser', product: 'Salicylic Acid Face Wash',
        example: cleanser, why: 'Deep cleanses pores', badges: getProductBadges(cleanser), ...info });
    } else if (skinType === 'dry') {
      if (budgetTier === 'premium') {
        cleanser = korean ? 'Laneige Cream Skin Refiner Cleanser' : indian ? 'Forest Essentials Hydrating Cleanser' : 'La Roche-Posay Toleriane Cleanser';
        cleanser = korean ? 'Innisfree Green Tea Cleansing Foam' : indian ? 'Plum Green Tea Gentle Face Wash' : 'Cetaphil Gentle Skin Cleanser';
      } else {
        cleanser = 'Cetaphil Gentle Skin Cleanser';
        if (korean) cleanser = 'Innisfree Green Tea Cleansing Foam';
        else if (indian) cleanser = 'Plum Green Tea Gentle Face Wash';
      }
      const info = getProductInfo(cleanser, 'Cleanser');
      recs.morning.push({ step: '1', name: 'Cleanser', product: 'Hydrating Cream Cleanser',
        example: cleanser, why: 'Gentle without drying', badges: getProductBadges(cleanser), ...info });
      recs.evening.push({ step: '1', name: 'Cleanser', product: 'Hydrating Cream Cleanser',
        example: cleanser, why: 'Maintains moisture', badges: getProductBadges(cleanser), ...info });
    } else if (skinType === 'combination') {
      if (budgetTier === 'premium') {
        cleanser = korean ? 'Beauty of Joseon Green Plum Cleanser' : indian ? 'Kama Ayurveda Face Cleanser' : 'La Roche-Posay Effaclar Cleanser';
        cleanser = korean ? 'COSRX Salicylic Acid Daily Gentle Cleanser' : indian ? 'Minimalist Salicylic Acid Face Wash' : 'Simple Refreshing Facial Wash Gel';
      } else {
        cleanser = 'Simple Refreshing Facial Wash Gel';
        if (korean) cleanser = 'COSRX Salicylic Acid Daily Gentle Cleanser';
        else if (indian) cleanser = 'Minimalist Salicylic Acid Face Wash';
      }
      const info = getProductInfo(cleanser, 'Cleanser');
      recs.morning.push({ step: '1', name: 'Cleanser', product: 'Gentle Face Wash',
        example: cleanser, why: 'Balances skin', badges: getProductBadges(cleanser), ...info });
      recs.evening.push({ step: '1', name: 'Cleanser', product: 'Gentle Face Wash',
        example: cleanser, why: 'Clean without stripping', badges: getProductBadges(cleanser), ...info });
    } else {
      if (budgetTier === 'premium') {
        cleanser = korean ? 'Laneige Multi Deep Clean Cleanser' : indian ? 'Forest Essentials Facial Cleanser' : 'La Roche-Posay Toleriane Cleanser';
        cleanser = korean ? 'Etude House Soon Jung Cleanser' : indian ? 'Plum Face Wash' : 'Simple Refreshing Facial Wash Gel';
      } else {
        cleanser = 'Simple Refreshing Facial Wash Gel';
        if (korean) cleanser = 'Etude House Soon Jung Cleanser';
        else if (indian) cleanser = 'WOW Skin Science Face Wash';
      }
      const info = getProductInfo(cleanser, 'Cleanser');
      recs.morning.push({ step: '1', name: 'Cleanser', product: 'Gentle Face Wash',
        example: cleanser, why: 'Balances skin', badges: getProductBadges(cleanser), ...info });
      recs.evening.push({ step: '1', name: 'Cleanser', product: 'Gentle Face Wash',
        example: cleanser, why: 'Clean without stripping', badges: getProductBadges(cleanser), ...info });
    }

    if (concerns.includes('Dark spots or uneven tone')) {
      let morn = 'Minimalist 10% Vitamin C Face Serum';
      let eve = 'Minimalist 10% Niacinamide Serum';
      if (budgetTier === 'premium') {
        morn = korean ? 'Beauty of Joseon Glow Serum' : indian ? 'Forest Essentials Vitamin C Serum' : 'La Roche-Posay Vitamin C Serum';
        eve = korean ? 'Some By Mi Galactomyces Pure Vitamin C Glow Serum' : indian ? 'Kama Ayurveda Brightening Serum' : 'Olay Regenerist Serum';
        morn = korean ? 'COSRX Triple C Lightning Liquid' : indian ? 'Dr. Sheth\'s Vitamin C Serum' : 'Neutrogena Rapid Tone Repair Serum';
        eve = korean ? 'Some By Mi Niacin Serum' : indian ? 'Pilgrim 10% Niacinamide Face Serum' : 'The Ordinary Niacinamide Serum';
      } else if (korean) {
        morn = 'COSRX Triple C Lightning Liquid';
        eve = 'Some By Mi Galactomyces Pure Vitamin C Glow Serum';
      } else if (indian) {
        morn = 'Plum 15% Vitamin C Face Serum';
        eve = 'Pilgrim 10% Niacinamide Face Serum';
      }
      const infoM = getProductInfo(morn, 'Serum');
      const infoE = getProductInfo(eve, 'Serum');
      recs.morning.push({ step: '2', name: 'Serum', product: 'Vitamin C Serum',
        example: morn, why: 'Brightens and evens tone', badges: getProductBadges(morn), ...infoM });
      recs.evening.push({ step: '2', name: 'Serum', product: 'Niacinamide Serum',
        example: eve, why: 'Reduces dark spots', badges: getProductBadges(eve), ...infoE });
    } else if (concerns.includes('Pimples and breakouts')) {
      let morn = 'Minimalist 10% Niacinamide Face Serum';
      let eve = 'The Ordinary Salicylic Acid 2% Solution';
      if (budgetTier === 'premium') {
        morn = korean ? 'Beauty of Joseon Propolis Serum' : indian ? 'Kama Ayurveda Clear Skin Serum' : 'La Roche-Posay Effaclar Serum';
        eve = korean ? 'Some By Mi AHA BHA PHA 30 Days Miracle Serum' : indian ? 'Forest Essentials Acne Control Serum' : 'La Roche-Posay Effaclar Duo';
        morn = korean ? 'COSRX BHA Blackhead Power Liquid' : indian ? 'Dr. Sheth\'s Niacinamide Serum' : 'Neutrogena Hydro Boost Serum';
        eve = korean ? 'Some By Mi AHA BHA PHA 30 Days Miracle Serum' : indian ? 'Minimalist 2% Salicylic Acid Serum' : 'The Ordinary Salicylic Acid 2% Solution';
      } else if (korean) {
        morn = 'COSRX BHA Blackhead Power Liquid';
        eve = 'Some By Mi AHA BHA PHA 30 Days Miracle Serum';
      } else if (indian) {
        morn = 'Pilgrim 10% Niacinamide Face Serum';
        eve = 'Minimalist 2% Salicylic Acid Serum';
      }
      const infoM = getProductInfo(morn, 'Serum');
      const infoE = getProductInfo(eve, 'Treatment');
      recs.morning.push({ step: '2', name: 'Serum', product: 'Niacinamide Serum',
        example: morn, why: 'Controls oil', badges: getProductBadges(morn), ...infoM });
      recs.evening.push({ step: '2', name: 'Treatment', product: 'Salicylic Acid Serum',
        example: eve, why: 'Treats pimples', badges: getProductBadges(eve), ...infoE });
    } else if (concerns.includes('Fine lines or aging signs') || age === '36-45' || age === '46+') {
      let morn = 'Minimalist 2% Hyaluronic Acid Serum';
      let eve = isPreg ? 'Minimalist 2% Hyaluronic Acid Serum' : 'Minimalist 0.3% Retinol Face Serum';
      if (budgetTier === 'premium') {
        morn = korean ? 'Laneige Water Bank Essence' : indian ? 'Forest Essentials Anti-Aging Serum' : 'La Roche-Posay Hyalu B5 Serum';
        eve = isPreg ? (korean ? 'Laneige Water Bank Essence' : indian ? 'Kama Ayurveda Rejuvenating Serum' : 'Olay Regenerist Retinol 24') : (korean ? 'Some By Mi Retinol Intense Reactivating Serum' : indian ? 'Forest Essentials Retinol Serum' : 'Olay Regenerist Retinol 24');
        morn = korean ? 'COSRX Advanced Snail 96 Mucin Power Essence' : indian ? 'Plum 2% Hyaluronic Acid Face Serum' : 'Neutrogena Hydro Boost Serum';
        eve = isPreg ? (korean ? 'Innisfree Jeju Orchid Enriched Essence' : indian ? 'Mamaearth Skin Illuminate Face Serum' : 'Neutrogena Rapid Wrinkle Repair') : (korean ? 'Some By Mi Retinol Intense Reactivating Serum' : indian ? 'Minimalist 0.3% Retinol Face Serum' : 'The Ordinary Retinol');
      } else if (korean) {
        morn = 'COSRX Advanced Snail 96 Mucin Power Essence';
        eve = isPreg ? 'Innisfree Jeju Orchid Enriched Essence' : 'Some By Mi Retinol Intense Reactivating Serum';
      } else if (indian) {
        morn = 'Plum 2% Hyaluronic Acid Face Serum';
        eve = isPreg ? 'Mamaearth Skin Illuminate Face Serum' : 'Minimalist 0.3% Retinol Face Serum';
      }
      const infoM = getProductInfo(morn, 'Serum');
      const infoE = getProductInfo(eve, 'Night Serum');
      recs.morning.push({ step: '2', name: 'Serum', product: 'Hyaluronic Acid Serum',
        example: morn, why: 'Plumps skin', badges: getProductBadges(morn), ...infoM });
      recs.evening.push({ step: '2', name: 'Night Serum', product: isPreg ? 'Hyaluronic Acid' : 'Retinol Serum',
        example: eve, why: isPreg ? 'Deep hydration' : 'Reduces wrinkles', badges: getProductBadges(eve), ...infoE });
    } else {
      let ser = 'Minimalist 2% Hyaluronic Acid Serum';
      if (budgetTier === 'premium') {
        ser = korean ? 'Laneige Water Bank Essence' : indian ? 'Forest Essentials Hydrating Serum' : 'La Roche-Posay Hyalu B5 Serum';
        ser = korean ? 'COSRX Advanced Snail 96 Mucin Power Essence' : indian ? 'Plum 2% Hyaluronic Acid Face Serum' : 'Neutrogena Hydro Boost Serum';
      } else if (korean) {
        ser = 'COSRX Advanced Snail 96 Mucin Power Essence';
      } else if (indian) {
        ser = 'Plum 2% Hyaluronic Acid Face Serum';
      }
      const info = getProductInfo(ser, 'Serum');
      recs.morning.push({ step: '2', name: 'Serum', product: 'Hyaluronic Acid Serum',
        example: ser, why: 'Keeps skin hydrated', badges: getProductBadges(ser), ...info });
    }

    const moistStep = recs.morning.length + 1;
    if (skinType === 'oily') {
      let moist = 'Neutrogena Hydro Boost Water Gel';
      if (budgetTier === 'premium') {
        moist = korean ? 'Laneige Water Bank Gel Cream' : indian ? 'Forest Essentials Gel Moisturizer' : 'La Roche-Posay Effaclar Mat';
        moist = korean ? 'COSRX Oil-Free Lotion' : indian ? 'Dr. Sheth\'s Oil-Free Moisturizer' : 'Neutrogena Hydro Boost Water Gel';
      } else if (korean) {
        moist = 'COSRX Oil-Free Lotion';
      } else if (indian) {
        moist = 'Minimalist Gel Moisturizer';
      }
      const info = getProductInfo(moist, 'Oil-Free Gel Moisturizer');
      recs.morning.push({ step: moistStep.toString(), name: 'Moisturizer', product: 'Oil-Free Gel Moisturizer',
        example: moist, why: 'Lightweight hydration', badges: getProductBadges(moist), ...info });
      recs.evening.push({ step: moistStep.toString(), name: 'Moisturizer', product: 'Oil-Free Gel Moisturizer',
        example: moist, why: 'Locks in treatment', badges: getProductBadges(moist), ...info });
    } else if (skinType === 'dry') {
      let morn = 'Cetaphil Moisturizing Cream';
      let eve = 'Ponds Night Cream';
      if (budgetTier === 'premium') {
        morn = korean ? 'Laneige Water Bank Moisture Cream' : indian ? 'Forest Essentials Intensive Moisture Cream' : 'La Roche-Posay Toleriane Rich Cream';
        eve = korean ? 'Laneige Water Sleeping Mask' : indian ? 'Kama Ayurveda Night Cream' : 'Olay Regenerist Night Recovery';
        morn = korean ? 'Laneige Water Bank Cream' : indian ? 'Plum Green Tea Moisturizer' : 'Cetaphil Moisturizing Cream';
        eve = korean ? 'Etude House Collagen Cream' : indian ? 'Mamaearth Night Cream' : 'Ponds Night Cream';
      } else if (korean) {
        morn = 'Laneige Water Bank Cream';
        eve = 'Etude House Collagen Cream';
      } else if (indian) {
        morn = 'Plum Green Tea Moisturizer';
        eve = 'Mamaearth Night Cream';
      }
      const infoM = getProductInfo(morn, 'Rich Hydrating Cream');
      const infoE = getProductInfo(eve, 'Night Cream');
      recs.morning.push({ step: moistStep.toString(), name: 'Moisturizer', product: 'Rich Hydrating Cream',
        example: morn, why: 'Deep moisture', badges: getProductBadges(morn), ...infoM });
      recs.evening.push({ step: moistStep.toString(), name: 'Night Cream', product: 'Nourishing Night Cream',
        example: eve, why: 'Repairs overnight', badges: getProductBadges(eve), ...infoE });
    } else {
      let morn = 'Simple Light Moisturizer';
      let eve = 'Nivea Night Cream';
      if (budgetTier === 'premium') {
        morn = korean ? 'Beauty of Joseon Dynasty Cream' : indian ? 'Forest Essentials Day Cream' : 'La Roche-Posay Toleriane Double Repair';
        eve = korean ? 'Laneige Water Sleeping Mask' : indian ? 'Kama Ayurveda Night Cream' : 'Olay Regenerist Night Recovery';
        morn = korean ? 'COSRX Snail Cream' : indian ? 'Dot & Key Moisturizer' : 'Simple Light Moisturizer';
        eve = korean ? 'Innisfree Green Tea Sleeping Mask' : indian ? 'Plum Night Gel' : 'Nivea Night Cream';
      } else if (korean) {
        morn = 'COSRX Snail Cream';
        eve = 'Innisfree Green Tea Sleeping Mask';
      } else if (indian) {
        morn = 'Dot & Key Moisturizer';
        eve = 'Plum Night Gel';
      }
      const infoM = getProductInfo(morn, 'Light Daily Moisturizer');
      const infoE = getProductInfo(eve, 'Night Cream');
      recs.morning.push({ step: moistStep.toString(), name: 'Moisturizer', product: 'Light Daily Moisturizer',
        example: morn, why: 'Perfect hydration', badges: getProductBadges(morn), ...infoM });
      recs.evening.push({ step: moistStep.toString(), name: 'Night Cream', product: 'Night Moisturizer',
        example: eve, why: 'Supports skin repair', badges: getProductBadges(eve), ...infoE });
    }

    const sunStep = recs.morning.length + 1;
    let sunscreen = 'Neutrogena Ultra Sheer SPF 50';
    if (climate && climate.includes('Hot')) {
      if (budgetTier === 'premium') {
        sunscreen = korean ? 'Beauty of Joseon Relief Sun SPF 50' : indian ? 'Forest Essentials SPF 50' : 'La Roche-Posay Anthelios SPF 50';
        sunscreen = korean ? 'COSRX Aloe Sun Cream SPF 50' : indian ? 'Requil Ultra Matte SPF 50' : 'Neutrogena Ultra Sheer SPF 50';
      } else {
        sunscreen = 'Requil Ultra Matte SPF 50';
        if (korean) sunscreen = 'COSRX Aloe Sun Cream SPF 50';
        else if (indian) sunscreen = 'Minimalist SPF 50';
      }
    } else {
      if (budgetTier === 'premium') {
        sunscreen = korean ? 'Beauty of Joseon Relief Sun SPF 50' : indian ? 'Kama Ayurveda Sun Defense SPF 50' : 'La Roche-Posay Anthelios SPF 50';
        sunscreen = korean ? 'Beauty of Joseon Relief Sun SPF 50' : indian ? 'Dot & Key Sunscreen SPF 50' : 'Neutrogena Ultra Sheer SPF 50';
      } else {
        if (korean) sunscreen = 'Beauty of Joseon Relief Sun SPF 50';
        else if (indian) sunscreen = 'Dot & Key Sunscreen SPF 50';
      }
    }
    const info = getProductInfo(sunscreen, 'Sunscreen');
    recs.morning.push({ step: sunStep.toString(), name: 'Sunscreen', product: 'Sunscreen SPF 50',
      example: sunscreen, why: 'Essential sun protection', badges: getProductBadges(sunscreen), ...info });

    return recs;
  };

  const currentQuestion = questions[step];
  const isComplete = step >= questions.length;

  const shouldSkipClimateQuestion = step === 3 && currentQuestion.id === 'climate' && responses.city && responses.city !== 'Other';

  if (shouldSkipClimateQuestion && !isComplete) {
    setTimeout(() => setStep(4), 0);
  }

  // Fetch AI recommendations when questionnaire is complete
  useEffect(() => {
    if (isComplete && !aiRecommendations && !isLoadingRecommendations) {
      fetchAIRecommendations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  // Rotate tips while loading
  useEffect(() => {
    if (isLoadingRecommendations) {
      const interval = setInterval(() => {
        setCurrentTipIndex((prevIndex) => (prevIndex + 1) % allTips.length);
      }, 4000); // Change tip every 4 seconds

      return () => clearInterval(interval);
    }
  }, [isLoadingRecommendations, allTips.length]);

  // Scroll to top when results page loads
  useEffect(() => {
    if (isComplete) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isComplete]);

  if (showHome) {
    return (
      <PageContainer maxWidth="6xl">
        <div className="text-center py-8 sm:py-12">
          {/* Hero Section - Redesigned */}
          <div className="animate-scale-in">
            <div className="inline-block">
              <GradientIcon Icon={Sparkles} size="lg" className="mb-6" />
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight animate-fade-in delay-100 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">LuminSkin</div>
            <div className="text-gray-900 text-3xl sm:text-4xl lg:text-5xl mt-2">
              GenAI-Powered Skincare <span className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">Recommendations</span>
            </div>
          </h1>

          <p className="text-xl sm:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto font-medium animate-fade-in delay-200">
            A demo project showcasing how <span className="font-semibold bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">Generative AI</span> personalizes skincare routines using <span className="font-semibold text-violet-700">GPT-4o-mini</span>, <span className="font-semibold text-gray-900">dermatological data</span>, and <span className="font-semibold text-gray-900">context-aware generation</span>
          </p>

          <div className="animate-scale-in delay-300 mb-8">
            <GradientButton onClick={() => setShowHome(false)} variant="primary">
              Try the Demo
              <ArrowRight className="inline-block ml-2 w-6 h-6" />
            </GradientButton>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-20 animate-fade-in delay-500">
            <Badge icon="✓">No Signup Required</Badge>
            <Badge icon="⚡">Powered by OpenAI</Badge>
            <Badge icon="🇮🇳">Built for India</Badge>
          </div>

          {/* Section Divider */}
          <div className="max-w-4xl mx-auto mb-20">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* How It Works Section */}
          <div className="mt-20 mb-20">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              How It <span className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">Works</span>
            </h2>
            <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
              Three simple steps to your perfect skincare routine
            </p>

            <div className="grid sm:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <motion.div
                className="animate-fade-in delay-600 relative"
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
              >
                <div className="glass-strong rounded-2xl p-8 border-2 border-violet-200 shadow-lg hover:shadow-2xl hover:border-violet-400 transition-all">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-pink-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4 mx-auto glow-gradient">
                    1
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-3">Tell Us About Your Skin</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Answer 11 quick questions about your skin type, concerns, climate, and preferences. Takes just 2 minutes!
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="animate-fade-in delay-700 relative"
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
              >
                <div className="glass-strong rounded-2xl p-8 border-2 border-violet-200 shadow-lg hover:shadow-2xl hover:border-violet-400 transition-all">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-pink-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4 mx-auto glow-gradient">
                    2
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-3">Get AI-Powered Results</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Our AI analyzes 50+ factors to create your custom morning & evening routines with the perfect products.
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="animate-fade-in delay-800 relative"
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
              >
                <div className="glass-strong rounded-2xl p-8 border-2 border-violet-200 shadow-lg hover:shadow-2xl hover:border-violet-400 transition-all">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-pink-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4 mx-auto glow-gradient">
                    3
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-3">Shop & Start Glowing</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Choose between budget & premium options for each product. Buy directly from Nykaa or Tira with one click!
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Section Divider */}
          <div className="max-w-4xl mx-auto mb-20">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* What the AI Analyzes */}
          <div className="mt-20 mb-20">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              What the AI <span className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">Analyzes</span>
            </h2>
            <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
              50+ factors considered to generate personalized recommendations
            </p>

            <div className="grid sm:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-sm border border-blue-200 hover:shadow-md transition-shadow">
                <div className="text-4xl mb-4">🧬</div>
                <h3 className="font-bold text-xl text-gray-900 mb-3">Skin Biology</h3>
                <ul className="text-gray-700 text-sm space-y-2 leading-relaxed">
                  <li>• Skin type (oily/dry/combo/sensitive)</li>
                  <li>• Existing conditions (acne, eczema, rosacea)</li>
                  <li>• Previous product reactions</li>
                  <li>• Age-appropriate active ingredients</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 shadow-sm border border-purple-200 hover:shadow-md transition-shadow">
                <div className="text-4xl mb-4">🌡️</div>
                <h3 className="font-bold text-xl text-gray-900 mb-3">Environment</h3>
                <ul className="text-gray-700 text-sm space-y-2 leading-relaxed">
                  <li>• Climate (hot/humid, hot/dry, monsoon)</li>
                  <li>• City-specific pollution levels</li>
                  <li>• Hard water considerations</li>
                  <li>• Seasonal variations</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 shadow-sm border border-amber-200 hover:shadow-md transition-shadow">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="font-bold text-xl text-gray-900 mb-3">Preferences</h3>
                <ul className="text-gray-700 text-sm space-y-2 leading-relaxed">
                  <li>• Budget constraints (₹1500-8000)</li>
                  <li>• Vegan/cruelty-free filters</li>
                  <li>• Fragrance-free requirements</li>
                  <li>• Brand diversity (50+ brands)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section Divider */}
          <div className="max-w-4xl mx-auto mb-20">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* Tech Stack */}
          <div className="mt-20 mb-20 gradient-bg-light rounded-3xl p-8 sm:p-12 max-w-5xl mx-auto border-2 border-violet-200 shadow-xl">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 text-center text-gray-900">
              Built With <span className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">Modern Tech</span>
            </h2>
            <p className="text-gray-600 mb-10 text-center max-w-2xl mx-auto">
              A full-stack AI application demonstrating real-world implementation
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="glass-strong rounded-xl p-6 border-2 border-violet-200 shadow-lg">
                <h3 className="font-bold text-lg text-violet-600 mb-4">Frontend</h3>
                <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
                  <li>⚛️ <span className="font-semibold">React 19</span> - Modern UI with hooks</li>
                  <li>🎨 <span className="font-semibold">Tailwind CSS</span> - Responsive design</li>
                  <li>🚀 <span className="font-semibold">Vercel</span> - Fast global deployment</li>
                </ul>
              </div>

              <div className="glass-strong rounded-xl p-6 border-2 border-pink-200 shadow-lg">
                <h3 className="font-bold text-lg text-pink-600 mb-4">Backend</h3>
                <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
                  <li>🤖 <span className="font-semibold">OpenAI GPT-4o-mini</span> - AI engine</li>
                  <li>⚡ <span className="font-semibold">Vercel Edge Functions</span> - Serverless API</li>
                  <li>🔒 <span className="font-semibold">CORS-enabled</span> - Secure requests</li>
                </ul>
              </div>

              <div className="glass-strong rounded-xl p-6 border-2 border-violet-200 shadow-lg">
                <h3 className="font-bold text-lg text-violet-600 mb-4">AI Features</h3>
                <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
                  <li>📝 <span className="font-semibold">4000+ token prompts</span> - Detailed instructions</li>
                  <li>🎯 <span className="font-semibold">JSON mode</span> - Structured outputs</li>
                  <li>💡 <span className="font-semibold">Few-shot learning</span> - Consistent quality</li>
                </ul>
              </div>

              <div className="glass-strong rounded-xl p-6 border-2 border-pink-200 shadow-lg">
                <h3 className="font-bold text-lg text-pink-600 mb-4">Data & Logic</h3>
                <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
                  <li>🧪 <span className="font-semibold">Dermatology protocols</span> - Science-backed</li>
                  <li>🇮🇳 <span className="font-semibold">50+ Indian brands</span> - Local market focus</li>
                  <li>💰 <span className="font-semibold">Dual pricing</span> - Budget + Premium tiers</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section Divider */}
          <div className="max-w-4xl mx-auto mb-20">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* FAQ Section */}
          <div className="mt-20 mb-20 max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-8 text-center">
              Frequently Asked <span className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">Questions</span>
            </h2>

            <div className="space-y-4">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="font-bold text-lg text-gray-900 mb-2">How does LuminSkin work?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  LuminSkin uses OpenAI's GPT-4o-mini with a 4000+ token prompt containing dermatological protocols, Indian brand data, and climate-specific guidelines. It analyzes your inputs and generates personalized product recommendations in structured JSON format.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="font-bold text-lg text-gray-900 mb-2">Is LuminSkin a commercial product?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  No, LuminSkin is a portfolio/demo project showcasing AI implementation in real-world scenarios. It's completely free to use and not affiliated with any skincare brand. The goal is to demonstrate personalized recommendation systems.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="font-bold text-lg text-gray-900 mb-2">What makes LuminSkin India-specific?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  LuminSkin's AI considers Indian climate zones (hot/humid, hot/dry, monsoon), pollution levels in major cities, hard water issues, and recommends from 50+ brands easily available on Nykaa and Tira with realistic Indian pricing.
                </p>
              </div>
            </div>
          </div>

        </div>
      </PageContainer>
    );
  }

  if (isComplete) {
    // Show loading state while fetching AI recommendations
    if (isLoadingRecommendations) {
      return (
        <PageContainer maxWidth="6xl">
          <div className="min-h-screen flex items-center justify-center py-12">
            <div className="max-w-3xl w-full">
              {/* Header with Spinner */}
              <div className="text-center mb-12">
                <div className="inline-block mb-6">
                  <div className="relative">
                    {/* Animated gradient ring */}
                    <motion.div
                      className="w-24 h-24 rounded-full"
                      style={{
                        background: 'conic-gradient(from 0deg, #9333ea, #ec4899, #9333ea)',
                      }}
                      animate={{
                        rotate: 360
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    >
                      <div className="w-full h-full rounded-full flex items-center justify-center">
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                          <motion.div
                            animate={{
                              scale: [1, 1.2, 1]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            <Sparkles className="w-10 h-10 bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent" style={{ fill: 'url(#gradient)' }} />
                            <svg width="0" height="0">
                              <defs>
                                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#9333ea" />
                                  <stop offset="100%" stopColor="#ec4899" />
                                </linearGradient>
                              </defs>
                            </svg>
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">
                  Creating Your <span className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">LuminSkin</span> Routine
                </h2>
                <p className="text-lg text-gray-600 mb-2">
                  Hang tight! We're analyzing <span className="font-bold bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">50+ factors</span> to personalize your results
                </p>
                <p className="text-sm text-gray-500">
                  This may take up to a minute...
                </p>
              </div>

              {/* Rotating Tip Card */}
              <motion.div
                className="glass-strong rounded-2xl p-8 shadow-2xl border-2 border-violet-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <motion.div
                      className="w-12 h-12 bg-gradient-to-r from-violet-600 to-pink-600 rounded-full flex items-center justify-center glow-gradient"
                      animate={{
                        rotate: [0, 360]
                      }}
                      transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    >
                      <span className="text-white text-2xl">💡</span>
                    </motion.div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-800 mb-3">
                      Skincare Tip #{currentTipIndex + 1}
                    </h3>
                    <p
                      key={currentTipIndex}
                      className="text-gray-700 leading-relaxed animate-fade-in"
                    >
                      {allTips[currentTipIndex]}
                    </p>
                  </div>
                </div>

                {/* Progress Dots */}
                <div className="flex justify-center gap-1.5 mt-6">
                  {[...Array(Math.min(allTips.length, 10))].map((_, idx) => (
                    <motion.div
                      key={idx}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        idx === currentTipIndex % 10
                          ? 'w-8 bg-gradient-to-r from-violet-600 to-pink-600'
                          : 'w-1.5 bg-gray-300'
                      }`}
                      animate={idx === currentTipIndex % 10 ? { scale: [1, 1.1, 1] } : {}}
                      transition={{ duration: 0.3 }}
                    />
                  ))}
                </div>
              </motion.div>

              {/* Fun Facts */}
              <div className="mt-8 text-center">
                <p className="text-sm text-gray-500 italic">
                  Did you know? Your personalized routine considers your climate, age, skin type, concerns, and preferences!
                </p>
              </div>
            </div>
          </div>
        </PageContainer>
      );
    }

    // Show error if AI recommendations failed
    if (error && !aiRecommendations) {
      return (
        <PageContainer maxWidth="6xl">
          <div className="text-center py-20">
            <div className="text-6xl mb-4">😔</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <GradientButton onClick={fetchAIRecommendations} variant="primary">
              Try Again
            </GradientButton>
          </div>
        </PageContainer>
      );
    }

    if (!aiRecommendations) {
      return null; // Should not reach here
    }

    return (
      <PageContainer maxWidth="6xl">
        {/* Header Section */}
        <motion.div
          className="text-center mb-8 sm:mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-pink-600 rounded-2xl blur-xl opacity-30" />
            <div className="relative w-16 h-16 bg-gradient-to-br from-violet-600 to-pink-600 rounded-2xl flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3 bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">
            Your Personalized Routine
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            Tailored for your unique skin needs
          </p>
        </motion.div>

        {/* Tabbed Results Component */}
        <TabbedResults
          aiRecommendations={aiRecommendations}
          getNykaaSearchUrl={getNykaaSearchUrl}
          getTiraSearchUrl={getTiraSearchUrl}
        />

        {/* Combined Important Information & Medical Advice */}
        {(aiRecommendations.disclaimer || aiRecommendations.when_to_see_doctor) && (
          <div className="mt-8 mb-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border-2 border-gray-300 shadow-lg animate-fade-in">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="text-2xl">⚠️</span>
              Important Information & Medical Advice
              <span className="text-2xl">🩺</span>
            </h3>

            {aiRecommendations.disclaimer && (
              <div className="mb-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <span className="font-semibold">Disclaimer:</span> {aiRecommendations.disclaimer}
                </p>
              </div>
            )}

            {aiRecommendations.when_to_see_doctor && (
              <div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  <span className="font-semibold">When to See a Doctor:</span> {aiRecommendations.when_to_see_doctor}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Start Over Button */}
        <div className="mb-8">
          <GradientButton
            onClick={() => {
              setStep(0);
              setShowHome(true);
              setResponses({
                gender: '', age: '', city: '', climate: '', shininess: '', dryness: '',
                concerns: [], conditions: [], reactions: '',
                routine: '', preferences: []
              });
              setAiRecommendations(null);
              setError(null);
            }}
            variant="primary"
            fullWidth
          >
            Start Over
          </GradientButton>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="2xl">
      <div className="animate-fade-in">
        <PageHeader
          Icon={Sparkles}
          title="Find Your LuminSkin Routine"
          subtitle="Answer a few simple questions"
          size="sm"
        />
      </div>

      <div className="animate-fade-in delay-100">
        <ProgressBar current={step + 1} total={questions.length} showPercentage={true} />
      </div>

        <motion.div
          className="glass-strong rounded-2xl shadow-2xl p-8 border-2 border-violet-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="text-xl font-bold text-gray-900 mb-2">{currentQuestion.question}</h2>
          {currentQuestion.description && <p className="text-gray-500 mb-6 text-sm">{currentQuestion.description}</p>}

          {currentQuestion.type === 'dropdown' ? (
            <div>
              <select value={responses[currentQuestion.id] || ''} onChange={(e) => handleResponse(currentQuestion.id, e.target.value)}
                className="w-full p-4 text-base border-2 border-gray-300 rounded-xl font-medium focus:border-[#FF7E6B] focus:outline-none appearance-none bg-white cursor-pointer"
                style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center"}}>
                <option value="">Select your city</option>
                {currentQuestion.options.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
              </select>
              
              {responses.city && responses.city !== 'Other' && (
                <div className="mt-4 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border-2 border-teal-200">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">
                      {responses.climate?.includes('Humid') && '☀️💧'}
                      {responses.climate?.includes('Dry') && !responses.climate?.includes('Humid') && '☀️'}
                      {responses.climate?.includes('Moderate') && '🌤️'}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 text-lg">Climate: {responses.climate}</h4>
                      <p className="text-sm text-gray-600 mt-1">Products suited for your weather</p>
                    </div>
                  </div>
                </div>
              )}
              
              {responses.city === 'Other' && (
                <div className="mt-4 p-4 bg-amber-50 rounded-xl border-2 border-amber-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Note:</span> Select climate type in next question
                  </p>
                </div>
              )}
            </div>
          ) : currentQuestion.id === 'climate' && responses.city === 'Other' ? (
            <div className="space-y-3">
              <p className="text-sm text-[#FF7E6B] font-medium mb-4">Select your climate:</p>
              {currentQuestion.options.map((option, idx) => {
                const isSelected = responses[currentQuestion.id] === option;
                return (
                  <motion.button
                    key={idx}
                    onClick={() => handleResponse(currentQuestion.id, option)}
                    className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                      isSelected ? 'border-violet-600 bg-gradient-to-r from-violet-50 to-pink-50 shadow-lg' : 'border-gray-300 hover:border-violet-300 hover:bg-violet-50'}`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                        isSelected ? 'border-violet-600 bg-gradient-to-br from-violet-600 to-pink-600' : 'border-gray-300'}`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div className="font-medium text-gray-800">{option}</div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          ) : (
            <div>
              <div className="space-y-3">
                {currentQuestion.options.map((option, idx) => {
                  const optValue = typeof option === 'string' ? option : option.value;
                  const optLabel = typeof option === 'string' ? option : option.label;
                  const isSelected = currentQuestion.type === 'multiple'
                    ? (responses[currentQuestion.id] || []).includes(optValue)
                    : responses[currentQuestion.id] === optValue;

                  return (
                    <motion.button
                      key={idx}
                      onClick={() => handleResponse(currentQuestion.id, optValue)}
                      className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                        isSelected ? 'border-violet-600 bg-gradient-to-r from-violet-50 to-pink-50 shadow-lg' : 'border-gray-300 hover:border-violet-300 hover:bg-violet-50'}`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                          isSelected ? 'border-violet-600 bg-gradient-to-br from-violet-600 to-pink-600' : 'border-gray-300'}`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <div className="font-medium text-gray-800">{optLabel}</div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {currentQuestion.id === 'shininess' && responses.shininess && (
                <motion.div
                  className="mt-4 p-4 glass-violet rounded-xl border-2 border-violet-200 shadow-md"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">💡 Tip: </span>
                    {responses.shininess === 'very-shiny' && 'Oily skin needs oil-control and lightweight products'}
                    {responses.shininess === 'tzone-shiny' && 'Combination skin needs balanced products for different areas'}
                    {responses.shininess === 'normal' && 'You have normal skin - lucky you! Maintain balance'}
                    {responses.shininess === 'tight' && 'Dry skin needs extra hydration and nourishment'}
                  </p>
                </motion.div>
              )}

              {currentQuestion.id === 'concerns' && responses.concerns && responses.concerns.length > 0 && (
                <motion.div
                  className="mt-4 p-4 glass-pink rounded-xl border-2 border-pink-200 shadow-md"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-sm text-gray-700 mb-2">
                    <span className="font-semibold">✨ Selected Concerns: </span>
                    {responses.concerns.join(', ')}
                  </p>
                  <p className="text-xs text-gray-600">
                    We'll recommend targeted treatments for these issues!
                  </p>
                </motion.div>
              )}

              {currentQuestion.id === 'routine' && responses.routine && (
                <motion.div
                  className="mt-4 p-4 glass-violet rounded-xl border-2 border-violet-200 shadow-md"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">⏰ Time Commitment: </span>
                    {responses.routine === 'minimal' && '5 minutes - Quick & efficient routine'}
                    {responses.routine === 'moderate' && '10 minutes - Balanced care'}
                    {responses.routine === 'comprehensive' && '15+ minutes - Complete pampering'}
                  </p>
                </motion.div>
              )}

              {currentQuestion.id === 'preferences' && responses.preferences && responses.preferences.length > 0 && (
                <motion.div
                  className="mt-4 p-4 glass-pink rounded-xl border-2 border-pink-200 shadow-md"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-sm text-gray-700 mb-2">
                    <span className="font-semibold">🌿 Your Preferences: </span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {responses.preferences.map((pref, i) => (
                      <span key={i} className="text-xs px-3 py-1 bg-pink-100 text-pink-800 rounded-full font-medium">
                        {pref}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Products will be filtered based on your preferences
                  </p>
                </motion.div>
              )}
            </div>
          )}

          <div className="flex gap-4 mt-8">
            {step > 0 && (
              <button onClick={() => {
                // If coming from step 4 (shininess) and city is selected (not "Other"), skip climate question (step 3)
                if (step === 4 && responses.city && responses.city !== 'Other') {
                  setStep(2); // Go back to city question instead
                } else {
                  setStep(step - 1);
                }
              }}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all flex items-center justify-center">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </button>
            )}
            <motion.button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all flex items-center justify-center ${
                canProceed() ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white hover:from-violet-700 hover:to-pink-700 hover:shadow-xl shadow-lg glow-hover-gradient'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'}`}
              whileHover={canProceed() ? { scale: 1.02 } : {}}
              whileTap={canProceed() ? { scale: 0.98 } : {}}
            >
              {step === questions.length - 1 ? 'Get My Routine' : 'Next'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </motion.button>
        </div>
      </motion.div>
    </PageContainer>
  );
};

export default SkincareRecommender;